import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  LayoutDashboard, ReceiptText, Package, Users, WalletCards, ChartNoAxesCombined, Settings,
  Search, Bell, Moon, Sun, Plus, LogOut, Menu, X, Truck, ShoppingCart, Zap, TrendingUp, Lock, ClipboardList, ListChecks, UserCog, Upload, Building2, CircleUserRound, Eye, FileStack, Crown, FileMinus2, FilePlus2, Command as CommandIcon,
} from "lucide-react";
import { api, money, istToday } from "../lib/api";
import { applyFilter } from "../lib/filters";
import { FlowSphereLogo } from "../components/FlowSphereLogo";
import Dashboard from "./Dashboard";
import ModuleView from "./ModuleView";
import Reports from "./Reports";
import Automations from "./Automations";
import DailyPricing from "./DailyPricing";
import SetupStatus from "./SetupStatus";
import SetupRequestsView from "../admin/SetupRequestsView";
import TeamView from "../admin/TeamView";
import ClientAccountsView from "../admin/ClientAccountsView";
import UpgradeRequestsView from "../admin/UpgradeRequestsView";
import AdminOverview from "../admin/AdminOverview";
import PackagesView from "../admin/PackagesView";
import BillingView from "../admin/BillingView";
import CreateModal from "../modals/CreateModal";
import ImportModal from "../modals/ImportModal";
import InvoicePreview from "../modals/InvoicePreview";
import SettingsModal from "../modals/SettingsModal";
import AccountModal from "../modals/AccountModal";
import CommandPalette from "../modals/CommandPalette";
import StatementModal from "../modals/StatementModal";

const STAFF_ROLES = ["admin", "master", "team"];

const NAV = [
  { id: "dashboard", label: "Overview", icon: LayoutDashboard },
  { id: "invoices", label: "Invoices", icon: ReceiptText },
  { id: "sales_orders", label: "Sales Orders", icon: FileStack },
  { id: "credit_notes", label: "Credit Notes", icon: FileMinus2 },
  { id: "inventory", label: "Inventory", icon: Package },
  { id: "customers", label: "Customers", icon: Users },
  { id: "suppliers", label: "Suppliers", icon: Truck },
  { id: "purchases", label: "Purchases", icon: ShoppingCart },
  { id: "debit_notes", label: "Debit Notes", icon: FilePlus2 },
  { id: "expenses", label: "Expenses", icon: WalletCards },
  { id: "reports", label: "Reports", icon: ChartNoAxesCombined },
  { id: "automations", label: "Automations", icon: Zap, pro: true },
  { id: "daily_pricing", label: "Daily Pricing", icon: TrendingUp, pro: true },
  { id: "setup_status", label: "Business Setup", icon: ListChecks, setup: true },
  { id: "setup_requests", label: "Setup Requests", icon: ClipboardList, staff: true },
  { id: "upgrade_requests", label: "Upgrade Requests", icon: Crown, master: true },
  { id: "clients", label: "Client Accounts", icon: Building2, clientAccess: true },
  { id: "team", label: "Team", icon: UserCog, master: true },
];

// The master account is an admin console — no client-business modules (inventory, invoices, etc.).
const ADMIN_NAV = [
  { id: "admin_overview", label: "Overview", icon: LayoutDashboard },
  { id: "packages", label: "Subscription Packages", icon: Package },
  { id: "billing", label: "Billing", icon: ReceiptText },
  { id: "clients", label: "Client Accounts", icon: Building2 },
  { id: "setup_requests", label: "Setup Requests", icon: ClipboardList },
  { id: "upgrade_requests", label: "Upgrade Requests", icon: Crown },
  { id: "team", label: "Team", icon: UserCog },
];

const TITLES = {
  dashboard: ["Here's what's moving today", "A calm overview of your entire business."],
  invoices: ["Invoices", "Create, send and track every sale."],
  sales_orders: ["Sales Orders", "Confirm a sale, then generate its invoice in one click."],
  credit_notes: ["Credit Notes", "Returns and refunds that reverse a customer's balance."],
  debit_notes: ["Debit Notes", "Purchase returns and adjustments that reduce what you owe suppliers."],
  inventory: ["Inventory", "Stay ahead of every stock movement."],
  customers: ["Customers", "Relationships that keep business moving."],
  suppliers: ["Suppliers", "Partners who keep your shelves full."],
  purchases: ["Purchase orders", "Restock with a clear paper trail."],
  expenses: ["Expenses", "Keep your spending visible and intentional."],
  reports: ["Reports", "A clearer picture of business performance."],
  automations: ["Automations", "Let WhatsApp reminders and follow-ups run themselves."],
  daily_pricing: ["Daily pricing", "Update volatile prices daily and revalue inventory automatically."],
  setup_status: ["Business setup", "Follow the progress of your guided workspace configuration."],
  setup_requests: ["Business Setup Requests", "Review, configure and hand over concierge customer workspaces."],
  upgrade_requests: ["Upgrade Requests", "Starter clients asking to move to Pro — activate them here."],
  clients: ["Client Accounts", "View every client workspace and open any one of them from here."],
  team: ["Team", "Invite internal reviewers who can process business setup requests."],
  admin_overview: ["Admin overview", "Your clients, subscriptions and requests at a glance."],
  packages: ["Subscription Packages", "Create and manage the plans you offer your clients."],
  billing: ["Billing", "Subscription invoices you raise to clients, and auto-generated renewals."],
};

const CREATE_MAP = {
  dashboard: "invoice",
  invoices: "invoice",
  sales_orders: "sales_order",
  credit_notes: "credit_note",
  debit_notes: "debit_note",
  inventory: "product",
  customers: "customer",
  suppliers: "supplier",
  purchases: "purchase",
  expenses: "expense",
};

const EDIT_TYPE = { customers: "customer", suppliers: "supplier", inventory: "product", invoices: "invoice", sales_orders: "sales_order", purchases: "purchase", expenses: "expense" };

const EXPORT_COLUMNS = {
  customers: [["name", "Name"], ["company", "Company"], ["email", "Email"], ["phone", "Phone"], ["address", "Address"], ["tax_id", "Tax ID"], ["balance", "Balance"]],
  inventory: [["name", "Name"], ["sku", "SKU"], ["category", "Category"], ["price", "Price"], ["cost", "Cost"], ["stock", "Stock"], ["reorder_level", "Reorder level"], ["unit", "Unit"]],
  suppliers: [["name", "Name"], ["company", "Company"], ["email", "Email"], ["phone", "Phone"], ["address", "Address"], ["payment_terms", "Payment terms"]],
  invoices: [["number", "Invoice"], ["customer_name", "Customer"], ["status", "Status"], ["due_date", "Due date"], ["subtotal", "Subtotal"], ["tax", "Tax"], ["total", "Total"], ["paid", "Paid"]],
  sales_orders: [["number", "SO number"], ["customer_name", "Customer"], ["status", "Status"], ["expected_delivery", "Delivery"], ["subtotal", "Subtotal"], ["tax", "Tax"], ["total", "Total"], ["invoice_number", "Invoice"]],
  credit_notes: [["number", "Credit note"], ["customer_name", "Customer"], ["invoice_number", "Invoice"], ["subtotal", "Subtotal"], ["tax", "Tax"], ["total", "Total"], ["reason", "Reason"], ["date", "Date"]],
  debit_notes: [["number", "Debit note"], ["supplier_name", "Supplier"], ["purchase_order_number", "PO"], ["subtotal", "Subtotal"], ["tax", "Tax"], ["total", "Total"], ["reason", "Reason"], ["date", "Date"]],
  purchases: [["number", "PO number"], ["supplier_name", "Supplier"], ["status", "Status"], ["expected_delivery", "Delivery"], ["total", "Total"]],
  expenses: [["description", "Description"], ["category", "Category"], ["amount", "Amount"], ["date", "Date"], ["payment_method", "Method"]],
};

const csvCell = (value) => {
  const text = value == null ? "" : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export default function Workspace({ user: initialUser, onLogout }) {
  const [user, setUser] = useState(initialUser);
  const [active, setActive] = useState(initialUser.role === "master" ? "admin_overview" : "dashboard");
  const [dark, setDark] = useState(false);
  const [data, setData] = useState(null);
  const [mobileNav, setMobileNav] = useState(false);
  const [currency, setCurrency] = useState("INR");
  const [modal, setModal] = useState(null);
  const [importType, setImportType] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [sectionFilter, setSectionFilter] = useState("all");
  const [switching, setSwitching] = useState(false);
  const [query, setQuery] = useState("");
  const [showCommand, setShowCommand] = useState(false);
  const [statementTarget, setStatementTarget] = useState(null);

  const refresh = () => api.get("/dashboard").then((response) => setData(response.data));
  const refreshUser = () => api.get("/auth/me").then((response) => setUser(response.data));

  const openClient = async (options = {}) => {
    setSwitching(true);
    try {
      await refreshUser();
      await refresh();
      setActive(options.section || "dashboard");
      if (options.createInvoice) setModal("invoice");
    } finally {
      setSwitching(false);
    }
  };

  const returnToMaster = async () => {
    setSwitching(true);
    try {
      await api.post("/master/stop-impersonate");
      await refreshUser();
      await refresh();
      setActive("clients");
      toast.success("Back to your master account");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not return to your account");
    } finally {
      setSwitching(false);
    }
  };

  useEffect(() => { document.body.classList.toggle("dark", dark); }, [dark]);
  useEffect(() => { refresh().catch(() => toast.error("Could not load dashboard data")); }, []);
  useEffect(() => { setSectionFilter("all"); }, [active]);

  const heading = TITLES[active] || [active, ""];
  const greeting = (active === "dashboard" || active === "admin_overview")
    ? [`Good day, ${user.name?.split(" ")[0] || "there"}`, active === "admin_overview" ? "Your clients, subscriptions and requests at a glance." : "A calm overview of your entire business."]
    : heading;

  const isPro = user.plan === "pro";
  const canManageClients = user.role === "master" || (user.permissions || []).includes("manage_clients");
  const masterConsole = user.role === "master" && !user.impersonating;
  useEffect(() => {
    if (masterConsole) return;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setShowCommand((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [masterConsole]);
  const navItems = masterConsole
    ? ADMIN_NAV
    : NAV.filter((item) => (!item.staff || STAFF_ROLES.includes(user.role)) && (!item.master || user.role === "master") && (!item.clientAccess || canManageClients) && (!item.setup || user.setup_request_id));
  const adminPages = ["admin_overview", "packages", "billing", "reports", "automations", "daily_pricing", "setup_requests", "setup_status", "team", "clients", "upgrade_requests"];

  const openCreate = () => setModal(CREATE_MAP[active] || "customer");

  const sectionItems = (source = filtered) => {
    if (active === "purchases") return source.purchase_orders || [];
    if (active === "sales_orders") return source.sales_orders || [];
    if (active === "credit_notes") return source.credit_notes || [];
    if (active === "debit_notes") return source.debit_notes || [];
    const key = { customers: "customers", inventory: "products", suppliers: "suppliers", invoices: "invoices", expenses: "expenses" }[active];
    return source[key] || [];
  };

  const exportSection = () => {
    const columns = EXPORT_COLUMNS[active];
    if (!columns) { toast.error("This section can't be exported"); return; }
    const rows = applyFilter(active, sectionItems(), sectionFilter);
    if (!rows.length) { toast.error("Nothing to export in this section yet"); return; }
    const header = columns.map(([, label]) => csvCell(label)).join(",");
    const body = rows.map((row) => columns.map(([key]) => csvCell(row[key])).join(",")).join("\n");
    const blob = new Blob([`${header}\n${body}\n`], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${active}-export-${istToday()}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} ${active} to CSV`);
  };

  const notifications = useMemo(() => {
    if (!data) return [];
    const list = [];
    (data.invoices || []).forEach((invoice) => {
      if (invoice.status === "Overdue") list.push({ id: `inv-${invoice.id}`, type: "overdue", title: `${invoice.number} is overdue`, detail: `${invoice.customer_name} · ${money(invoice.total - invoice.paid, currency)} due`, go: "invoices" });
    });
    (data.products || []).forEach((product) => {
      if (product.stock <= product.reorder_level) list.push({ id: `stk-${product.id}`, type: "stock", title: `${product.name} is low on stock`, detail: `${product.stock} ${product.unit}s left · reorder at ${product.reorder_level}`, go: "inventory" });
    });
    return list;
  }, [data, currency]);

  const filtered = useMemo(() => {
    if (!data) return { customers: [], products: [], invoices: [], expenses: [], suppliers: [], purchase_orders: [], payments: [] };
    if (!query.trim()) return data;
    const q = query.toLowerCase();
    const filter = (list, keys) => list.filter((item) => keys.some((key) => `${item[key] ?? ""}`.toLowerCase().includes(q)));
    return {
      ...data,
      customers: filter(data.customers || [], ["name", "company", "email", "phone"]),
      products: filter(data.products || [], ["name", "sku", "category"]),
      invoices: filter(data.invoices || [], ["number", "customer_name", "status"]),
      expenses: filter(data.expenses || [], ["category", "description"]),
      suppliers: filter(data.suppliers || [], ["name", "company", "email"]),
      purchase_orders: filter(data.purchase_orders || [], ["number", "supplier_name", "status"]),
      sales_orders: filter(data.sales_orders || [], ["number", "customer_name", "status"]),
      credit_notes: filter(data.credit_notes || [], ["number", "customer_name", "invoice_number", "reason"]),
      debit_notes: filter(data.debit_notes || [], ["number", "supplier_name", "purchase_order_number", "reason"]),
      payments: filter(data.payments || [], ["invoice_number", "customer_name", "method"]),
    };
  }, [data, query]);

  const onUpgraded = () => { refreshUser(); refresh(); };

  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileNav ? "open" : ""}`}>
        <div className="sidebar-top">
          <div className="brand-mark">
            <FlowSphereLogo variant="sidebar" />
          </div>
          <button data-testid="close-sidebar-button" className="icon-button mobile-only" onClick={() => setMobileNav(false)}><X size={18} /></button>
        </div>
        <div className="workspace-switcher">
          <div className="avatar">{user.name?.[0] || "A"}</div>
          <div>
            <strong>{user.name || "Workspace owner"}</strong>
            <small><span className={`plan-pill ${isPro ? "pro" : "basic"}`} data-testid="plan-pill">{isPro ? "Pro" : "Starter"}</span> {user.impersonating ? "· Client view" : user.is_demo ? "· Master" : "· INR"}</small>
          </div>
        </div>
        <p className="nav-label">WORKSPACE</p>
        <nav>
          {navItems.map((item) => {
            const Icon = item.icon;
            const badge = item.id === "invoices" ? (data?.invoices || []).filter((i) => i.status === "Overdue" || i.status === "Partially Paid").length : null;
            const locked = item.pro && !isPro;
            return (
              <button data-testid={`nav-${item.id}-button`} className={`${active === item.id ? "active" : ""} ${locked ? "locked" : ""}`} key={item.id} onClick={() => { setActive(item.id); setMobileNav(false); }}>
                <Icon size={18} />{item.label}
                {locked && <Lock size={12} className="nav-lock" />}
                {!!badge && <b>{badge}</b>}
              </button>
            );
          })}
        </nav>
        <p className="nav-label lower">MANAGE</p>
        <button data-testid="nav-account-button" className="nav-link" onClick={() => setShowAccount(true)}>
          <CircleUserRound size={18} />Account
        </button>
        <button data-testid="nav-settings-button" className="nav-link" onClick={() => setShowSettings(true)}>
          <Settings size={18} />Settings
        </button>
        <div className="sidebar-bottom">
          <div className="health">
            <span></span>
            <div><strong>All systems healthy</strong><small>Last synced just now</small></div>
          </div>
          <button data-testid="logout-button" className="logout-button" onClick={onLogout}><LogOut size={16} />Sign out</button>
        </div>
      </aside>
      {mobileNav && <div className="nav-backdrop" data-testid="nav-backdrop" onClick={() => setMobileNav(false)} />}
      <section className="main-area">
        {user.impersonating && (
          <div className="impersonation-banner" data-testid="impersonation-banner">
            <span><Eye size={15} /> Viewing <strong>{data?.profile?.business_name || user.name}</strong> as {user.master?.name || "master"} — changes you make apply to this client's workspace.</span>
            <button data-testid="return-to-master-button" disabled={switching} onClick={returnToMaster}>
              {switching ? "Returning…" : "Return to my account"}
            </button>
          </div>
        )}
        <header className="topbar">
          <button data-testid="open-sidebar-button" className="icon-button mobile-only" onClick={() => setMobileNav(true)}><Menu size={20} /></button>
          {!masterConsole && (
            <div className="search-box">
              <Search size={17} />
              <input data-testid="global-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search customers, invoices, products…" />
              <button type="button" className="cmdk-hint" data-testid="command-palette-trigger" onClick={() => setShowCommand(true)} title="Open command palette (Ctrl/⌘ + K)">
                <CommandIcon size={12} /> K
              </button>
            </div>
          )}
          {masterConsole && <div style={{ flex: 1 }} />}
          <div className="top-actions">
            <select data-testid="currency-select" value={currency} onChange={(e) => setCurrency(e.target.value)}>
              <option>INR</option><option>USD</option>
            </select>
            <button data-testid="theme-toggle-button" className="icon-button" onClick={() => setDark(!dark)}>{dark ? <Sun size={18} /> : <Moon size={18} />}</button>
            <div className="notification-wrap">
              <button data-testid="notifications-button" className="icon-button notification" onClick={() => setShowNotifications((v) => !v)}>
                <Bell size={18} />{notifications.length > 0 && <i></i>}
              </button>
              {showNotifications && (
                <>
                  <div className="dropdown-backdrop" onClick={() => setShowNotifications(false)} />
                  <div className="notifications-menu" data-testid="notifications-menu">
                    <div className="notifications-head"><strong>Notifications</strong><span>{notifications.length}</span></div>
                    {notifications.length === 0 ? (
                      <p className="notifications-empty" data-testid="notifications-empty">You're all caught up</p>
                    ) : (
                      <div className="notifications-list">
                        {notifications.map((note) => (
                          <button key={note.id} className="notification-item" data-testid={`notification-${note.id}`} onClick={() => { setActive(note.go); setShowNotifications(false); }}>
                            <span className={`note-dot ${note.type}`}></span>
                            <span><strong>{note.title}</strong><small>{note.detail}</small></span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="top-avatar">{user.name?.[0] || "A"}</div>
          </div>
        </header>
        <main className="content">
          <div className="page-heading">
            <div>
              <p className="eyebrow">{active === "dashboard" ? "TODAY" : `FLOWSPHERE / ${active.replace("_", " ").toUpperCase()}`}</p>
              <h1 data-testid="page-title">{greeting[0]}</h1>
              <p>{greeting[1]}</p>
            </div>
            <div className="heading-actions">
              {active === "dashboard" ? (
                <>
                  <button data-testid="dashboard-create-sales-order" className="secondary-button" onClick={() => setModal("sales_order")}><Plus size={15} /> Sales order</button>
                  <button data-testid="dashboard-create-purchase-order" className="secondary-button" onClick={() => setModal("purchase")}><Plus size={15} /> Purchase order</button>
                  <button data-testid="dashboard-create-customer" className="secondary-button" onClick={() => setModal("customer")}><Plus size={15} /> Customer</button>
                  <button data-testid="dashboard-create-supplier" className="primary-button" onClick={() => setModal("supplier")}><Plus size={15} /> Supplier</button>
                </>
              ) : !adminPages.includes(active) && (
                <>
                  {["customers", "inventory", "suppliers"].includes(active) && (
                    <button data-testid="import-button" className="secondary-button" onClick={() => setImportType(active)}>
                      <Upload size={15} /> Import
                    </button>
                  )}
                  <button data-testid="export-button" className="secondary-button" onClick={exportSection}>Export</button>
                  <button data-testid="create-primary-button" className="primary-button" onClick={openCreate}>
                    <Plus size={17} /> {({ invoices: "Create invoice", sales_orders: "Create sales order", credit_notes: "New credit note", debit_notes: "New debit note", inventory: "Add product", customers: "Add customer", suppliers: "Add supplier", purchases: "Create PO", expenses: "Record expense" })[active]}
                  </button>
                </>
              )}
            </div>
          </div>
          {active === "dashboard" && <Dashboard data={data} currency={currency} onPayment={() => setModal("payment")} onOpenInvoice={setPreview} onNavigate={setActive} />}
          {active === "admin_overview" && <AdminOverview onNavigate={setActive} />}
          {active === "packages" && <PackagesView />}
          {active === "billing" && <BillingView />}
          {active === "reports" && <Reports currency={currency} />}
          {active === "automations" && <Automations user={user} onUpgraded={onUpgraded} />}
          {active === "daily_pricing" && <DailyPricing user={user} currency={currency} onUpgraded={onUpgraded} />}
          {active === "setup_requests" && STAFF_ROLES.includes(user.role) && <SetupRequestsView />}
          {active === "upgrade_requests" && user.role === "master" && <UpgradeRequestsView />}
          {active === "clients" && canManageClients && <ClientAccountsView onOpenClient={openClient} />}
          {active === "team" && user.role === "master" && <TeamView />}
          {active === "setup_status" && <SetupStatus />}
          {!adminPages.includes(active) && active !== "dashboard" && (
            <ModuleView active={active} data={filtered} currency={currency} onOpenInvoice={setPreview} onRefresh={refresh} onCreate={openCreate} onImport={() => setImportType(active)} onEdit={(item) => setModal({ type: EDIT_TYPE[active], edit: item })} onStatement={(item) => setStatementTarget({ item, entity: active === "suppliers" ? "supplier" : "customer" })} onRefund={(item) => setModal({ type: "refund", customer: item })} onPayPO={(po) => setModal({ type: "supplier_payment", po })} filter={sectionFilter} onFilterChange={setSectionFilter} />
          )}
        </main>
      </section>
      {modal && <CreateModal type={modal} data={data} onClose={() => setModal(null)} onSaved={(payload) => { setModal(null); refresh(); if (payload?.preview) setPreview(payload.preview); }} />}
      {importType && <ImportModal type={importType} onClose={() => setImportType(null)} onImported={refresh} />}
      {preview && <InvoicePreview invoice={preview} profile={data?.profile} currency={currency} onClose={() => setPreview(null)} onRefresh={refresh} onRecordPayment={(invoice) => { setPreview(null); setModal({ type: "payment", invoice }); }} />}
      {showSettings && <SettingsModal profile={data?.profile} onClose={() => setShowSettings(false)} onSaved={refresh} />}
      {showAccount && <AccountModal user={user} onClose={() => setShowAccount(false)} onSaved={refreshUser} />}
      {!masterConsole && (
        <CommandPalette
          open={showCommand}
          onOpenChange={setShowCommand}
          data={data}
          currency={currency}
          onNavigate={(section) => { setActive(section); setMobileNav(false); }}
          onCreate={(t) => setModal(t)}
          onOpenInvoice={setPreview}
        />
      )}
      {statementTarget && <StatementModal customer={statementTarget.item} entity={statementTarget.entity} currency={currency} onRefund={statementTarget.entity === "customer" ? (cust) => { setStatementTarget(null); setModal({ type: "refund", customer: cust }); } : null} onClose={() => setStatementTarget(null)} />}
    </div>
  );
}
